/**
 * Task #2903 — Tabelle di riferimento FatturaPA / Agenzia delle Entrate.
 *
 * Fonte di verità unica (lato codice) degli enum statici del tracciato
 * FatturaPA (Provv. AdE 89757/2018 e succ. modifiche, specifiche tecniche
 * fatturapa.gov.it v1.9). Consumate da FE + backend; le tabelle DB globali
 * gemelle (migration `fiscal_reference_tables`) ne sono il riflesso persistito
 * e read-only.
 *
 * NB: il Regime fiscale (RF01-RF19) resta servito anche dalla tabella
 * `public.fiscal_tax_regimes` (con `loadFiscalTaxRegimes`), che porta i flag
 * runtime per-tenant (modalità di calcolo IVA). Qui dichiariamo l'elenco
 * canonico dei codici per i consumer statici (picker FE).
 */

/** Voce di riferimento canonica: codice + etichetta user-facing + descrizione. */
export interface FiscalReferenceEntry<TCode extends string> {
  readonly code: TCode;
  readonly label: string;
  readonly description: string;
}

// ---------------------------------------------------------------------------
// TipoDocumento (TD)
// ---------------------------------------------------------------------------
// I codici TD10-TD15 NON esistono nel tracciato FatturaPA: l'elenco salta da
// TD09 a TD16. Dichiariamo solo i 22 codici reali.

export const TIPO_DOCUMENTO_CODES = [
  'TD01', 'TD02', 'TD03', 'TD04', 'TD05', 'TD06', 'TD07', 'TD08', 'TD09',
  'TD16', 'TD17', 'TD18', 'TD19', 'TD20', 'TD21', 'TD22', 'TD23', 'TD24',
  'TD25', 'TD26', 'TD27', 'TD28',
] as const;
export type TipoDocumentoCode = typeof TIPO_DOCUMENTO_CODES[number];

export const TIPO_DOCUMENTO: ReadonlyArray<FiscalReferenceEntry<TipoDocumentoCode>> = [
  { code: 'TD01', label: 'Fattura', description: 'Fattura ordinaria.' },
  { code: 'TD02', label: 'Acconto/Anticipo su fattura', description: 'Acconto o anticipo su fattura.' },
  { code: 'TD03', label: 'Acconto/Anticipo su parcella', description: 'Acconto o anticipo su parcella.' },
  { code: 'TD04', label: 'Nota di credito', description: 'Nota di credito (storno/variazione in diminuzione).' },
  { code: 'TD05', label: 'Nota di debito', description: 'Nota di debito (variazione in aumento).' },
  { code: 'TD06', label: 'Parcella', description: 'Parcella professionale.' },
  { code: 'TD07', label: 'Fattura semplificata', description: 'Fattura semplificata (art. 21-bis DPR 633/72).' },
  { code: 'TD08', label: 'Nota di credito semplificata', description: 'Nota di credito semplificata.' },
  { code: 'TD09', label: 'Nota di debito semplificata', description: 'Nota di debito semplificata.' },
  { code: 'TD16', label: 'Integrazione reverse charge interno', description: 'Integrazione fattura reverse charge interno.' },
  { code: 'TD17', label: 'Integrazione/autofattura servizi estero', description: 'Integrazione/autofattura per acquisto servizi dall’estero.' },
  { code: 'TD18', label: 'Integrazione beni intracomunitari', description: 'Integrazione per acquisto di beni intracomunitari.' },
  { code: 'TD19', label: 'Integrazione/autofattura art.17 c.2', description: 'Integrazione/autofattura per acquisto di beni ex art.17 c.2 DPR 633/72.' },
  { code: 'TD20', label: 'Autofattura regolarizzazione', description: 'Autofattura per regolarizzazione e integrazione delle fatture (art.6 c.8 d.lgs.471/97 o art.46 c.5 DL 331/93).' },
  { code: 'TD21', label: 'Autofattura per splafonamento', description: 'Autofattura per splafonamento.' },
  { code: 'TD22', label: 'Estrazione beni da Deposito IVA', description: 'Estrazione beni da Deposito IVA.' },
  { code: 'TD23', label: 'Estrazione beni da Deposito IVA con versamento IVA', description: 'Estrazione beni da Deposito IVA con versamento dell’IVA.' },
  { code: 'TD24', label: 'Fattura differita art.21 c.4 lett.a)', description: 'Fattura differita di cui all’art.21 c.4 lett.a).' },
  { code: 'TD25', label: 'Fattura differita art.21 c.4 lett.b)', description: 'Fattura differita di cui all’art.21 c.4 terzo periodo lett.b).' },
  { code: 'TD26', label: 'Cessione beni ammortizzabili e passaggi interni', description: 'Cessione di beni ammortizzabili e per passaggi interni (ex art.36 DPR 633/72).' },
  { code: 'TD27', label: 'Fattura per autoconsumo o cessioni gratuite', description: 'Fattura per autoconsumo o per cessioni gratuite senza rivalsa.' },
  { code: 'TD28', label: 'Acquisti da San Marino con IVA (cartacea)', description: 'Acquisti da San Marino con IVA (fattura cartacea).' },
];

// ---------------------------------------------------------------------------
// Regime fiscale (RF) — elenco canonico codici. I flag runtime per-tenant
// (default_vat_calculation_modes) restano in public.fiscal_tax_regimes.
// ---------------------------------------------------------------------------

export const REGIME_FISCALE_CODES = [
  'RF01', 'RF02', 'RF04', 'RF05', 'RF06', 'RF07', 'RF08', 'RF09', 'RF10',
  'RF11', 'RF12', 'RF13', 'RF14', 'RF15', 'RF16', 'RF17', 'RF18', 'RF19',
] as const;
export type RegimeFiscaleCode = typeof REGIME_FISCALE_CODES[number];

export const REGIME_FISCALE: ReadonlyArray<FiscalReferenceEntry<RegimeFiscaleCode>> = [
  { code: 'RF01', label: 'Ordinario', description: 'Regime ordinario.' },
  { code: 'RF02', label: 'Contribuenti minimi', description: 'Contribuenti minimi (art.1 c.96-117 L.244/2007).' },
  { code: 'RF04', label: 'Agricoltura e attività connesse', description: 'Agricoltura e attività connesse e pesca (artt.34 e 34-bis DPR 633/72).' },
  { code: 'RF05', label: 'Vendita sali e tabacchi', description: 'Vendita sali e tabacchi (art.74 c.1 DPR 633/72).' },
  { code: 'RF06', label: 'Commercio dei fiammiferi', description: 'Commercio dei fiammiferi (art.74 c.1 DPR 633/72).' },
  { code: 'RF07', label: 'Editoria', description: 'Editoria (art.74 c.1 DPR 633/72).' },
  { code: 'RF08', label: 'Gestione servizi telefonia pubblica', description: 'Gestione di servizi di telefonia pubblica (art.74 c.1 DPR 633/72).' },
  { code: 'RF09', label: 'Rivendita documenti di trasporto', description: 'Rivendita di documenti di trasporto pubblico e di sosta (art.74 c.1 DPR 633/72).' },
  { code: 'RF10', label: 'Intrattenimenti e giochi', description: 'Intrattenimenti, giochi e altre attività (art.74 c.6 DPR 633/72).' },
  { code: 'RF11', label: 'Agenzie viaggi e turismo', description: 'Agenzie di viaggi e turismo (art.74-ter DPR 633/72).' },
  { code: 'RF12', label: 'Agriturismo', description: 'Agriturismo (art.5 c.2 L.413/91).' },
  { code: 'RF13', label: 'Vendite a domicilio', description: 'Vendite a domicilio (art.25-bis c.6 DPR 600/73).' },
  { code: 'RF14', label: 'Rivendita beni usati, arte, antiquariato', description: 'Rivendita di beni usati, oggetti d’arte, antiquariato o da collezione (art.36 DL 41/95).' },
  { code: 'RF15', label: 'Agenzie vendite all’asta arte/antiquariato', description: 'Agenzie di vendite all’asta di oggetti d’arte, antiquariato o da collezione (art.40-bis DL 41/95).' },
  { code: 'RF16', label: 'IVA per cassa P.A.', description: 'IVA per cassa P.A. (art.6 c.5 DPR 633/72).' },
  { code: 'RF17', label: 'IVA per cassa', description: 'IVA per cassa (art.32-bis DL 83/2012).' },
  { code: 'RF18', label: 'Altro', description: 'Altro.' },
  { code: 'RF19', label: 'Forfettario', description: 'Regime forfettario (art.1 c.54-89 L.190/2014).' },
];

// ---------------------------------------------------------------------------
// ModalitaPagamento (MP) — assi FatturaPA. NB: distinti dai codici scontrino
// 0-5 (AdePaymentCode in pos/fiscal-mapping.ts), che riguardano i corrispettivi.
// ---------------------------------------------------------------------------

export const MODALITA_PAGAMENTO_CODES = [
  'MP01', 'MP02', 'MP03', 'MP04', 'MP05', 'MP06', 'MP07', 'MP08', 'MP09',
  'MP10', 'MP11', 'MP12', 'MP13', 'MP14', 'MP15', 'MP16', 'MP17', 'MP18',
  'MP19', 'MP20', 'MP21', 'MP22', 'MP23',
] as const;
export type ModalitaPagamentoCode = typeof MODALITA_PAGAMENTO_CODES[number];

export const MODALITA_PAGAMENTO: ReadonlyArray<FiscalReferenceEntry<ModalitaPagamentoCode>> = [
  { code: 'MP01', label: 'Contanti', description: 'Contanti.' },
  { code: 'MP02', label: 'Assegno', description: 'Assegno.' },
  { code: 'MP03', label: 'Assegno circolare', description: 'Assegno circolare.' },
  { code: 'MP04', label: 'Contanti presso Tesoreria', description: 'Contanti presso Tesoreria.' },
  { code: 'MP05', label: 'Bonifico', description: 'Bonifico.' },
  { code: 'MP06', label: 'Vaglia cambiario', description: 'Vaglia cambiario.' },
  { code: 'MP07', label: 'Bollettino bancario', description: 'Bollettino bancario.' },
  { code: 'MP08', label: 'Carta di pagamento', description: 'Carta di pagamento.' },
  { code: 'MP09', label: 'RID', description: 'RID.' },
  { code: 'MP10', label: 'RID utenze', description: 'RID utenze.' },
  { code: 'MP11', label: 'RID veloce', description: 'RID veloce.' },
  { code: 'MP12', label: 'RIBA', description: 'Riba (Ricevuta Bancaria Elettronica).' },
  { code: 'MP13', label: 'MAV', description: 'MAV.' },
  { code: 'MP14', label: 'Quietanza erario', description: 'Quietanza erario stato.' },
  { code: 'MP15', label: 'Giroconto conti contabilità speciale', description: 'Giroconto su conti di contabilità speciale.' },
  { code: 'MP16', label: 'Domiciliazione bancaria', description: 'Domiciliazione bancaria.' },
  { code: 'MP17', label: 'Domiciliazione postale', description: 'Domiciliazione postale.' },
  { code: 'MP18', label: 'Bollettino di c/c postale', description: 'Bollettino di c/c postale.' },
  { code: 'MP19', label: 'SEPA Direct Debit', description: 'SEPA Direct Debit.' },
  { code: 'MP20', label: 'SEPA Direct Debit CORE', description: 'SEPA Direct Debit CORE.' },
  { code: 'MP21', label: 'SEPA Direct Debit B2B', description: 'SEPA Direct Debit B2B.' },
  { code: 'MP22', label: 'Trattenuta su somme già riscosse', description: 'Trattenuta su somme già riscosse.' },
  { code: 'MP23', label: 'PagoPA', description: 'PagoPA.' },
];

/**
 * Task #3189 — Modalità di pagamento "comuni" al banco: proprietà statica
 * della tabella di riferimento (NON configurazione per-tenant). L'ordine
 * dell'array è l'ordine di presentazione nel gruppo "Comuni" della select
 * a doppio strato (POS fattura rapida + InvoiceModal).
 */
export const MODALITA_PAGAMENTO_COMMON_CODES = [
  'MP01', 'MP08', 'MP05', 'MP02', 'MP12',
] as const satisfies ReadonlyArray<ModalitaPagamentoCode>;

/** Voci MP del gruppo "Comuni", nell'ordine di presentazione. */
export const MODALITA_PAGAMENTO_COMMON: ReadonlyArray<FiscalReferenceEntry<ModalitaPagamentoCode>> =
  MODALITA_PAGAMENTO_COMMON_CODES.map((code) => {
    const entry = MODALITA_PAGAMENTO.find((m) => m.code === code);
    if (!entry) throw new Error(`MP comune non presente in MODALITA_PAGAMENTO: ${code}`);
    return entry;
  });

/** Voci MP del gruppo "Altre modalità" (tutte le non-comuni, in ordine di codice). */
export const MODALITA_PAGAMENTO_OTHER: ReadonlyArray<FiscalReferenceEntry<ModalitaPagamentoCode>> =
  MODALITA_PAGAMENTO.filter((m) => !(MODALITA_PAGAMENTO_COMMON_CODES as readonly string[]).includes(m.code));

// ---------------------------------------------------------------------------
// TipoRitenuta (RT)
// ---------------------------------------------------------------------------

export const TIPO_RITENUTA_CODES = ['RT01', 'RT02', 'RT03', 'RT04', 'RT05', 'RT06'] as const;
export type TipoRitenutaCode = typeof TIPO_RITENUTA_CODES[number];

export const TIPO_RITENUTA: ReadonlyArray<FiscalReferenceEntry<TipoRitenutaCode>> = [
  { code: 'RT01', label: 'Ritenuta persone fisiche', description: 'Ritenuta d’acconto persone fisiche.' },
  { code: 'RT02', label: 'Ritenuta persone giuridiche', description: 'Ritenuta d’acconto persone giuridiche.' },
  { code: 'RT03', label: 'Contributo INPS', description: 'Contributo INPS.' },
  { code: 'RT04', label: 'Contributo ENASARCO', description: 'Contributo ENASARCO.' },
  { code: 'RT05', label: 'Contributo ENPAM', description: 'Contributo ENPAM.' },
  { code: 'RT06', label: 'Altro contributo previdenziale', description: 'Altro contributo previdenziale.' },
];

// ---------------------------------------------------------------------------
// TipoCassa (TC)
// ---------------------------------------------------------------------------

export const TIPO_CASSA_CODES = [
  'TC01', 'TC02', 'TC03', 'TC04', 'TC05', 'TC06', 'TC07', 'TC08', 'TC09',
  'TC10', 'TC11', 'TC12', 'TC13', 'TC14', 'TC15', 'TC16', 'TC17', 'TC18',
  'TC19', 'TC20', 'TC21', 'TC22',
] as const;
export type TipoCassaCode = typeof TIPO_CASSA_CODES[number];

export const TIPO_CASSA: ReadonlyArray<FiscalReferenceEntry<TipoCassaCode>> = [
  { code: 'TC01', label: 'Cassa avvocati/procuratori', description: 'Cassa nazionale previdenza e assistenza avvocati e procuratori legali.' },
  { code: 'TC02', label: 'Cassa dottori commercialisti', description: 'Cassa previdenza dottori commercialisti.' },
  { code: 'TC03', label: 'Cassa geometri', description: 'Cassa previdenza e assistenza geometri.' },
  { code: 'TC04', label: 'Cassa ingegneri/architetti', description: 'Cassa nazionale previdenza e assistenza ingegneri e architetti liberi professionisti.' },
  { code: 'TC05', label: 'Cassa notariato', description: 'Cassa nazionale del notariato.' },
  { code: 'TC06', label: 'Cassa ragionieri/periti commerciali', description: 'Cassa nazionale previdenza e assistenza ragionieri e periti commerciali.' },
  { code: 'TC07', label: 'ENASARCO', description: 'Ente nazionale assistenza agenti e rappresentanti di commercio (ENASARCO).' },
  { code: 'TC08', label: 'ENPACL', description: 'Ente nazionale previdenza e assistenza consulenti del lavoro (ENPACL).' },
  { code: 'TC09', label: 'ENPAM', description: 'Ente nazionale previdenza e assistenza medici (ENPAM).' },
  { code: 'TC10', label: 'ENPAF', description: 'Ente nazionale previdenza e assistenza farmacisti (ENPAF).' },
  { code: 'TC11', label: 'ENPAV', description: 'Ente nazionale previdenza e assistenza veterinari (ENPAV).' },
  { code: 'TC12', label: 'ENPAIA', description: 'Ente nazionale previdenza e assistenza impiegati dell’agricoltura (ENPAIA).' },
  { code: 'TC13', label: 'Fondo spedizionieri/agenzie marittime', description: 'Fondo previdenza impiegati imprese di spedizione e agenzie marittime.' },
  { code: 'TC14', label: 'INPGI', description: 'Istituto nazionale previdenza giornalisti italiani (INPGI).' },
  { code: 'TC15', label: 'ONAOSI', description: 'Opera nazionale assistenza orfani sanitari italiani (ONAOSI).' },
  { code: 'TC16', label: 'CASAGIT', description: 'Cassa autonoma assistenza integrativa giornalisti italiani (CASAGIT).' },
  { code: 'TC17', label: 'EPPI', description: 'Ente previdenza periti industriali e periti industriali laureati (EPPI).' },
  { code: 'TC18', label: 'EPAP', description: 'Ente previdenza e assistenza pluricategoriale (EPAP).' },
  { code: 'TC19', label: 'ENPAB', description: 'Ente nazionale previdenza e assistenza biologi (ENPAB).' },
  { code: 'TC20', label: 'ENPAPI', description: 'Ente nazionale previdenza e assistenza professione infermieristica (ENPAPI).' },
  { code: 'TC21', label: 'ENPAP', description: 'Ente nazionale previdenza e assistenza psicologi (ENPAP).' },
  { code: 'TC22', label: 'INPS', description: 'INPS.' },
];

// ---------------------------------------------------------------------------
// Esigibilità IVA
// ---------------------------------------------------------------------------

export const ESIGIBILITA_IVA_CODES = ['I', 'D', 'S'] as const;
export type EsigibilitaIvaCode = typeof ESIGIBILITA_IVA_CODES[number];

export const ESIGIBILITA_IVA: ReadonlyArray<FiscalReferenceEntry<EsigibilitaIvaCode>> = [
  { code: 'I', label: 'Esigibilità immediata', description: 'Esigibilità immediata dell’IVA.' },
  { code: 'D', label: 'Esigibilità differita', description: 'Esigibilità differita dell’IVA.' },
  { code: 'S', label: 'Scissione dei pagamenti', description: 'Scissione dei pagamenti (split payment).' },
];

// ---------------------------------------------------------------------------
// Lookup maps + type guards
// ---------------------------------------------------------------------------

function indexByCode<TCode extends string>(
  entries: ReadonlyArray<FiscalReferenceEntry<TCode>>,
): ReadonlyMap<TCode, FiscalReferenceEntry<TCode>> {
  const m = new Map<TCode, FiscalReferenceEntry<TCode>>();
  for (const e of entries) m.set(e.code, e);
  return m;
}

export const TIPO_DOCUMENTO_BY_CODE = indexByCode(TIPO_DOCUMENTO);
export const REGIME_FISCALE_BY_CODE = indexByCode(REGIME_FISCALE);
export const MODALITA_PAGAMENTO_BY_CODE = indexByCode(MODALITA_PAGAMENTO);
export const TIPO_RITENUTA_BY_CODE = indexByCode(TIPO_RITENUTA);
export const TIPO_CASSA_BY_CODE = indexByCode(TIPO_CASSA);
export const ESIGIBILITA_IVA_BY_CODE = indexByCode(ESIGIBILITA_IVA);

export function isTipoDocumentoCode(v: string): v is TipoDocumentoCode {
  return (TIPO_DOCUMENTO_CODES as readonly string[]).includes(v);
}
export function isRegimeFiscaleCode(v: string): v is RegimeFiscaleCode {
  return (REGIME_FISCALE_CODES as readonly string[]).includes(v);
}
export function isModalitaPagamentoCode(v: string): v is ModalitaPagamentoCode {
  return (MODALITA_PAGAMENTO_CODES as readonly string[]).includes(v);
}
export function isTipoRitenutaCode(v: string): v is TipoRitenutaCode {
  return (TIPO_RITENUTA_CODES as readonly string[]).includes(v);
}
export function isTipoCassaCode(v: string): v is TipoCassaCode {
  return (TIPO_CASSA_CODES as readonly string[]).includes(v);
}
export function isEsigibilitaIvaCode(v: string): v is EsigibilitaIvaCode {
  return (ESIGIBILITA_IVA_CODES as readonly string[]).includes(v);
}
